import { useParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Dialog,
  Typography,
  Button,
  IconButton,
  Stack,
  Divider,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  MODAL_HEIGHT,
  MODAL_LEFT_INSET,
  MODAL_RIGHT_MARGIN,
  MODAL_VERTICAL_INSET_SM,
  MODAL_WIDTH,
} from "../../../../../../layout/Header/components/CreateClientModal/shared";
import {
  MODAL_MAX_HEIGHT_XS,
  SAFE_AREA_BOTTOM,
  SAFE_AREA_TOP,
} from "../../../../../../layout/layoutMetrics";
import {
  HOME_GLASS_BACKDROP,
  HOME_GLASS_BORDER,
} from "../../../../../../pages/Home/constants/homeGlass";
import BasicDetailsStep from "./Steps/BasicDetailsStep";
import FilesStep from "./Steps/FilesSteps";
import VariantsStep from "./Steps/VariantsStep";
import ActivationPointsStep from "./Steps/ActivationPointStep";
import { deleteVariantById } from "../../../../../../core/services/projectWorkRoom.service";

const STEPS = [
  { title: "Basic Details", id: "basic", Icon: PersonOutlineIcon },
  { title: "Deliverables", id: "activation-points", Icon: FormatListBulletedIcon },
  { title: "Variants", id: "variants", Icon: CallSplitIcon },
  { title: "Files", id: "files", Icon: CloudUploadIcon },
];

const ACCENT = "#DBA529";

const emptyVariant = () => ({
  variantsTitle: "",
  variantsDescription: "",
  variantsPoints: [],
  hookName: "",
});

const buildInitialState = (initialData, projectId) => {
 
  const variants = initialData?.variants?.length
  ? initialData.variants.map((v) => ({ ...emptyVariant(), ...v }))
  : [];
  const data = {};
  data["name"] = initialData?.activationName ?? "";
  data["description"] =  initialData?.description ?? ""
  data["projectId"] = projectId ?? "";
  data["activationPoints"] = initialData?.activationPoints?.length
      ? [...initialData.activationPoints]
      : [];
  data["variants"] = variants;
  data["files"] = initialData?.fileName?.length ? [...initialData.fileName] : []
  if(initialData?.activationId) {
    data["activationId"] = initialData.activationId;
  }
  return data;
};

/**
 * ActivationForm
 * Create or update an activation.
 *
 * Props:
 * - open {boolean}
 * - handleClose {() => void}
 * - initialData {object|null}  pass an existing activation to edit it,
 *                              omit/null to create a new one
 * - onSubmit {(payload: object) => void}  called with the full form state
 *                                          when the final step is submitted
 * - submitting {boolean}  optional, disables actions while a save is in flight
 */
function ActivationForm(props) {
  const { open, handleClose, initialData = null, onSubmit, submitting = false, defaultStep = null } = props;
  const isEditMode = Boolean(initialData);
  const { projectId } = useParams();

  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(() => buildInitialState(initialData));
  const [pointDraft, setPointDraft] = useState("");
  const [variantPointDrafts, setVariantPointDrafts] = useState({});
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(buildInitialState(initialData, projectId));
      setActiveStep(0);
      setPointDraft("");
      setVariantPointDrafts({});
      if (defaultStep) {
        setActiveStep(defaultStep)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialData]);

  const updateField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const addActivationPoint = () => {
    const value = pointDraft.trim();
    if (!value) return;
    updateField("activationPoints", [...form.activationPoints, value]);
    setPointDraft("");
  };

  const removeActivationPoint = (index) => {
    updateField(
      "activationPoints",
      form.activationPoints.filter((_, i) => i !== index)
    );
  }

  const addVariant = () =>
    updateField("variants", [...form.variants, emptyVariant()]);

  const removeVariant = async (index, variantId) => {
    try {
      if(variantId){
        await deleteVariantById(variantId);
      }
    } catch (error) {
      console.error("Failed to delete variant:", error);
    } finally {
      updateField(
        "variants",
        form.variants.filter((_, i) => i !== index)
      );
    }
  }

  const updateVariantField = (index, field, value) =>
    updateField(
      "variants",
      form.variants.map((v, i) => (i === index ? { ...v, [field]: value } : v))
    );

  const addVariantPoint = (index) => {
    const draft = (variantPointDrafts[index] || "").trim();
    if (!draft) return;
    updateVariantField(index, "variantsPoints", [
      ...form.variants[index].variantsPoints,
      draft,
    ]);
    setVariantPointDrafts((prev) => ({ ...prev, [index]: "" }));
  };

  const removeVariantPoint = (variantIndex, pointIndex) => {
    updateVariantField(
      variantIndex,
      "variantsPoints",
      form.variants[variantIndex].variantsPoints.filter((_, i) => i !== pointIndex)
    );
  }

  const handleFilesSelected = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    updateField("files", [...form.files, ...incoming]);
  };

  const removeFile = (index) => {
    updateField(
      "files",
      form.files.filter((_, i) => i !== index)
    );
  }

  const isLastStep = activeStep === STEPS.length - 1;

  const canContinue = useMemo(() => {
    if (activeStep === 0) return form.name.trim().length > 0;
    return true;
  }, [activeStep, form.name]);

  const handleBack = () => {
    if (activeStep === 0) {
      handleClose();
      return;
    }
    setActiveStep((s) => s - 1);
  };

  const handleContinue = () => {
    if (!canContinue) return;
    if (isLastStep) {
      onSubmit?.(form, isEditMode);
      return;
    }
    setActiveStep((s) => s + 1);
  };

  return (
    <Dialog
      open={open}
      onClos={handleClose} 
      maxWidth={false}
      sx={{
        "& .MuiDialog-container": {
          justifyContent: "flex-start",
          alignItems: { xs: "center", sm: "stretch" },
          paddingLeft: { xs: 1, sm: MODAL_LEFT_INSET },
          paddingRight: { xs: 1, sm: MODAL_RIGHT_MARGIN },
          paddingTop: {
            xs: `max(8px, ${SAFE_AREA_TOP})`,
            sm: MODAL_VERTICAL_INSET_SM,
          },
          paddingBottom: {
            xs: `max(8px, ${SAFE_AREA_BOTTOM})`,
            sm: MODAL_VERTICAL_INSET_SM,
          },
          boxSizing: "border-box",
        },
      }}
      PaperProps={{
        sx: {
          m: 0,
          width: { xs: "calc(100vw - 16px)", sm: MODAL_WIDTH },
          height: { xs: "auto", sm: MODAL_HEIGHT },
          maxWidth: { xs: "min(100%, calc(100vw - 16px))", sm: MODAL_WIDTH },
          maxHeight: { xs: MODAL_MAX_HEIGHT_XS, sm: MODAL_HEIGHT },
          borderRadius: "16px",
          overflow: "hidden",
          background:
            "linear-gradient(145deg, rgba(64, 64, 53, 0.78), rgba(28, 29, 22, 0.85)), radial-gradient(circle at 0% 0%, rgba(219, 165, 41, 0.12), transparent 50%)",
          backdropFilter: HOME_GLASS_BACKDROP,
          WebkitBackdropFilter: HOME_GLASS_BACKDROP,
          border: HOME_GLASS_BORDER,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          color: "#fff",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            pt: 3,
            pb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(219, 165, 41, 0.16)",
                border: `1px solid rgba(219, 165, 41, 0.4)`,
              }}
            >
              <FormatListBulletedIcon sx={{ color: ACCENT, fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 20, lineHeight: 1.2 }}>
                {isEditMode ? "Update Activation" : "Create New Activation"}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>
                Step {activeStep + 1} of {STEPS.length} · {STEPS[activeStep].title}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: "rgba(255,255,255,0.7)" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Stepper */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 4,
            pb: 3,
          }}
        >
          {STEPS.map((step, index) => {
            const StepIcon = step.Icon;
            const isActive = index === activeStep;
            const isComplete = index < activeStep;
            return (
              <Box key={step.id} sx={{ display: "flex", alignItems: "center", flex: index === STEPS.length - 1 ? "0 0 auto" : 1 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: `2px solid ${isActive || isComplete ? ACCENT : "rgba(255,255,255,0.2)"}`,
                      background: isComplete ? ACCENT : "rgba(255,255,255,0.04)",
                      transition: "all 0.2s ease",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setActiveStep(index);
                    }}
                  >
                    <StepIcon
                      sx={{
                        fontSize: 20,
                        color: isComplete ? "#1c1d16" : isActive ? ACCENT : "rgba(255,255,255,0.5)",
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? "#fff" : "rgba(255,255,255,0.5)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {step.title}
                  </Typography>
                </Box>
                {index !== STEPS.length - 1 && (
                  <Box
                    sx={{
                      flex: 1,
                      height: "2px",
                      mx: 1.5,
                      mb: 2.5,
                      background: isComplete ? ACCENT : "rgba(255,255,255,0.15)",
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 4, py: 3 }}>
          {activeStep === 0 && (
            <BasicDetailsStep form={form} updateField={updateField} />
          )}
          {activeStep === 1 && (
            <ActivationPointsStep
              points={form.activationPoints}
              draft={pointDraft}
              setDraft={setPointDraft}
              onAdd={addActivationPoint}
              onRemove={removeActivationPoint}
            />
          )}
          {activeStep === 2 && (
            <VariantsStep
              variants={form.variants}
              variantPointDrafts={variantPointDrafts}
              setVariantPointDrafts={setVariantPointDrafts}
              onAddVariant={addVariant}
              onRemoveVariant={removeVariant}
              onUpdateVariantField={updateVariantField}
              onAddVariantPoint={addVariantPoint}
              onRemoveVariantPoint={removeVariantPoint}
            />
          )}
          {activeStep === 3 && (
            <FilesStep
              files={form.files}
              form={form}
              fileInputRef={fileInputRef}
              onFilesSelected={handleFilesSelected}
              onRemoveFile={removeFile}
            />
          )}
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2.5,
          }}
        >
          <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.5)" }}>
            Step {activeStep + 1} of {STEPS.length}
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <Button
              onClick={handleBack}
              disabled={submitting}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.2)",
                border: "1px solid",
                textTransform: "none",
                px: 2.5,
                borderRadius: "10px",
                "&:hover": { borderColor: "rgba(255,255,255,0.4)" },
              }}
            >
              {activeStep === 0 ? "Cancel" : "Back"}
            </Button>
            <Button
              onClick={handleContinue}
              disabled={!canContinue || submitting}
              endIcon={!isLastStep ? <ChevronRightIcon /> : null}
              sx={{
                background: ACCENT,
                color: "#1c1d16",
                textTransform: "none",
                fontWeight: 700,
                px: 3,
                borderRadius: "10px",
                "&:hover": { background: "#c79620" },
                "&.Mui-disabled": {
                  background: "rgba(219, 165, 41, 0.35)",
                  color: "rgba(28,29,22,0.6)",
                },
              }}
            >
              {isLastStep ? (isEditMode ? "Save Changes" : "Create Activation") : "Continue"}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Dialog>
  );
}
export default ActivationForm;
